import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { Group } from '../types/groups';
import { groupsApi } from '../services/api';

interface GroupsDataContextType {
  data: Group[];
  loading: boolean;
  error: Error | null;
  loadingMore: boolean;
  refetch: () => void;
  clearCache: () => void;
  getGroupById: (id: string) => Group | undefined;
}

const GroupsDataContext = createContext<GroupsDataContextType | undefined>(undefined);

interface GroupsDataProviderProps {
  children: ReactNode;
}

export const GroupsDataProvider: React.FC<GroupsDataProviderProps> = ({ children }) => {
  const [data, setData] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Track if data has been fetched to prevent duplicate calls
  const hasFetchedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchGroups = async (isRefetch: boolean = false) => {
    // If we already have data and this isn't a refetch, skip
    if (!isRefetch && hasFetchedRef.current && data.length > 0) {
      console.log('[GroupsDataContext] Using cached data');
      setLoading(false);
      return;
    }

    // Create abort controller for cleanup
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    try {
      setLoading(true);
      setError(null);
      
      // Mark as fetched before starting
      hasFetchedRef.current = true;
      
      // Step 1: Fetch first page immediately for fast initial render
      const firstPageResponse = await groupsApi.getGroupsFirstPage();
      
      // Check if aborted
      if (signal.aborted) {
        setLoading(false);
        return;
      }
      
      // Validate response structure
      if (!firstPageResponse || !firstPageResponse.data || !Array.isArray(firstPageResponse.data)) {
        console.error('Invalid response structure:', firstPageResponse);
        throw new Error(`Invalid response from API: ${JSON.stringify(firstPageResponse)}`);
      }
              
      // Data is already formatted by the BFF, just use it
      const firstPageGroups = firstPageResponse.data as unknown as Group[];
      setData(firstPageGroups);
      setLoading(false); // Stop initial loading spinner
      
      console.log('[GroupsDataContext] Loaded first page:', firstPageGroups.length, 'groups');
      
      // Step 2: If there are more pages, fetch them in the background
      if (firstPageResponse.hasMore) {
        setLoadingMore(true);          
        // Small delay to ensure UI renders first page first
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Check if aborted
        if (signal.aborted) {
          setLoadingMore(false);
          return;
        }
        
        const remainingResponse = await groupsApi.getGroupsRemainingPages(2);
        
        // Check if aborted
        if (signal.aborted) {
          setLoadingMore(false);
          return;
        }
        
        // Validate response structure
        if (!remainingResponse || !remainingResponse.data || !Array.isArray(remainingResponse.data)) {
          console.error('Invalid remaining pages response:', remainingResponse);
          // Don't throw, just log and continue with first page data
          setLoadingMore(false);
          console.log(`⚠️ Could not load remaining pages. Showing ${firstPageResponse.data.length} groups`);
          return;
        }
                  
        // Data is already formatted by the BFF, merge with existing data and deduplicate by ID
        const additionalGroups = remainingResponse.data as unknown as Group[];
        setData(prevData => {
          // Create a Map to deduplicate by ID
          const groupsMap = new Map<string, Group>();
          
          // Add existing groups
          prevData.forEach(group => groupsMap.set(group.id, group));
          
          // Add new groups (will overwrite if duplicate)
          let duplicateCount = 0;
          additionalGroups.forEach(group => {
            if (groupsMap.has(group.id)) {
              duplicateCount++;
            }
            groupsMap.set(group.id, group);
          });
          
          // Log warning if duplicates found
          if (duplicateCount > 0) {
            console.warn(
              `[GroupsDataContext] Found ${duplicateCount} duplicate group(s) when merging pages.`
            );
          }
          
          return Array.from(groupsMap.values());
        });
        setLoadingMore(false);
        
        const totalCount = firstPageResponse.data.length + remainingResponse.data.length;
        console.log(`✅ [GroupsDataContext] All data loaded! Total: ${totalCount} groups`);
      } else {
        console.log(`✅ [GroupsDataContext] All data loaded! Total: ${firstPageResponse.data.length} groups`);
      }
      
    } catch (err) {
      // Only update state if not aborted
      if (!signal.aborted) {
        console.error('[GroupsDataContext] Error fetching groups:', err);
        
        // Enhanced error logging
        if (err instanceof Error) {
          console.error('Error details:', {
            message: err.message,
            stack: err.stack,
          });
        }
        
        // Check if it's an Axios error with response
        const axiosError = err as any;
        if (axiosError?.response) {
          console.error('API Response Error:', {
            status: axiosError.response.status,
            statusText: axiosError.response.statusText,
            data: axiosError.response.data,
            headers: axiosError.response.headers,
          });
          
          // Create a more helpful error message
          const errorMessage = axiosError.response.data?.error?.message 
            || axiosError.response.data?.message 
            || `API Error: ${axiosError.response.status} ${axiosError.response.statusText}`;
          
          setError(new Error(errorMessage));
        } else if (axiosError?.request) {
          // Request was made but no response received
          console.error('No response from server. Is the BFF API running?');
          setError(new Error('Cannot connect to API server. Please ensure the BFF API is running.'));
        } else {
          setError(err instanceof Error ? err : new Error('Failed to fetch groups'));
        }
        
        setData([]);
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  useEffect(() => {
    fetchGroups();

    // Cleanup function
    return () => {
      // Abort any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []); // Empty dependency array - only run once

  const refetch = () => {
    hasFetchedRef.current = false;
    fetchGroups(true);
  };

  const clearCache = () => {
    setData([]);
    setLoading(true);
    setError(null);
    setLoadingMore(false);
    hasFetchedRef.current = false;
  };

  const value: GroupsDataContextType = {
    data,
    loading,
    error,
    loadingMore,
    refetch,
    clearCache,
    getGroupById: (id: string) => data.find(group => group.id === id),
  };

  return (
    <GroupsDataContext.Provider value={value}>
      {children}
    </GroupsDataContext.Provider>
  );
};

export const useGroupsDataContext = () => {
  const context = useContext(GroupsDataContext);
  if (context === undefined) {
    throw new Error('useGroupsDataContext must be used within a GroupsDataProvider');
  }
  return context;
};
